import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Modal,
    Fade,
    IconButton,
    CircularProgress,
    Grid,
    Link,
    Chip,
    InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { Order, OrderItem, InventoryItem } from '../models';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { collection, addDoc, writeBatch, increment, doc, getDoc, updateDoc, setDoc, Timestamp, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getUserRole } from '../utils/auth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { RouteGroup, Route } from '../models';
import { AddCircleOutline, AttachFile, Clear, LocalShipping, PictureAsPdf, RemoveCircleOutline, SettingsApplications, Edit, Upload } from '@mui/icons-material';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

interface OrderCreationModalProps {
    open: boolean;
    onClose: () => void;
    inventoryItems: InventoryItem[];
    existingOrder?: Order;
    onOrderCreated: (order: Order, noteAdmin?: string, noteDriver?: string, deliveryFileUrl?: string, container_values?: string[], lenght_container_values?: string) => void;
    currentRouteGroup?: RouteGroup;
    currentRoute?: Route;
    onRouteGroupUpdate?: (updatedRouteGroup: RouteGroup) => Promise<void>;
}

// Definir un tipo extendido localmente
interface ExtendedOrder extends Order {
    routeGroupId?: string;
    routeId?: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: theme.spacing(1),
}));

const FixedBottomBox = styled(Box)(({ theme }) => ({
    position: 'sticky',
    bottom: 0,
    // Make the background color more transparent or match the table
    backgroundColor: 'rgba(135, 206, 235, 0.15)',
    zIndex: 1,
    padding: theme.spacing(2),
    // Use a subtle border or shadow instead of a stark divider
    borderTop: 'none',
    boxShadow: '0px -2px 4px rgba(0,0,0,0.05)',
}));

const ProductImage = styled(Avatar)(({ theme }) => ({
    width: 60,
    height: 60,
    cursor: 'pointer',
    '&:hover': {
        boxShadow: theme.shadows[10],
    },
}));

const FullScreenImageModal = styled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const FullScreenImage = styled('img')({
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '8px',
    boxShadow: '0 0 24px rgba(0, 0, 0, 0.5)',
});

const successAnimation = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const SuccessIcon = styled(CheckCircleOutlineIcon)(({ theme }) => ({
    fontSize: 60,
    color: theme.palette.success.main,
    animation: `${successAnimation} 0.5s ease-out`,
}));

const OrderCreationModalAutomatic: React.FC<OrderCreationModalProps> = ({
    open,
    onClose,
    inventoryItems,
    existingOrder,
    onOrderCreated,
    currentRouteGroup,
    currentRoute,
    onRouteGroupUpdate
}) => {
    const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
    const [availableQuantities, setAvailableQuantities] = useState<{ [key: string]: number }>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [fullScreenImageOpen, setFullScreenImageOpen] = useState(false);
    const [fullScreenImageUrl, setFullScreenImageUrl] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [noteAdmin, setNoteAdmin] = useState<string>('');
    const [noteDriver, setNoteDriver] = useState<string>('');
    const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);
    const [formData, setFormData] = useState<{
        deliveryFile: File | null;
        numContainers: number;
        containerValues: string[];
    }>({
        deliveryFile: null,
        numContainers: 1,
        containerValues: [''],
    });

    useEffect(() => {
        if (existingOrder) {
            setOrderItems(existingOrder.items);
           
            const initialAvailableQuantities = inventoryItems.reduce((acc, item) => {
                const orderItem = existingOrder?.items.find(oi => oi.inventoryItemId === item.id);
                acc[item.id] = item.quantity;
                return acc;
            }, {} as { [key: string]: number });
            setAvailableQuantities(initialAvailableQuantities);
        }
    }, [existingOrder, inventoryItems]);

    useEffect(() => {
        if (currentRoute) {
            setNoteAdmin(currentRoute.note_admin || '');
            setNoteDriver(currentRoute.note_driver || '');
            setExistingFileUrl(currentRoute.deliveryFileUrl || null);
            setFormData({
                deliveryFile: currentRoute.deliveryFileUrl ? null : null, // Assuming you don't want to load the actual file, just the URL
                numContainers: currentRoute.container_values?.length || 1,
                containerValues: currentRoute.container_values || [''],
            });
        }
    }, [currentRoute]);

    const handleQuantityChange = useCallback((itemId: string, quantity: number) => {
        setOrderItems(prevItems => {
            const existingItemIndex = prevItems.findIndex(item => item.inventoryItemId === itemId);
            if (existingItemIndex > -1) {
                const updatedItems = [...prevItems];
                updatedItems[existingItemIndex] = { ...updatedItems[existingItemIndex], quantity };
                return updatedItems;
            } else {
                return [...prevItems, { inventoryItemId: itemId, quantity }];
            }
        });

        // setAvailableQuantities(prev => {
        //     const inventoryItem = inventoryItems.find(item => item.id === itemId); // inventoryItem is declared here
        //     const prevOrderItem = orderItems.find(item => item.inventoryItemId === itemId);
        //     const prevQuantity = prevOrderItem?.quantity || 0;
        //     const quantityDifference = quantity - prevQuantity; // Calculate the difference
        //     const initialQuantity = inventoryItem?.quantity || 0;

        //     return {
        //         ...prev,
        //         [itemId]: initialQuantity - quantityDifference // Subtract the difference
        //     };
        // });
    }, [inventoryItems]);


    const handleSave = async () => {
        setIsLoading(true);
        try {
            // Validar datos requeridos
            if (!currentRouteGroup?.id) {
                throw new Error('Se requiere el ID del grupo de rutas');
            }
            
            // Si hay una ruta actual pero no tiene ID, significa que es una nueva ruta
            // En este caso, no debemos lanzar un error ya que el ID se generará al guardar
            if (!currentRoute) {
                throw new Error('Se requiere una ruta para crear el pedido');
            }

            // Filtrar y validar items del pedido
            const updatedOrderItems: OrderItem[] = orderItems
                .filter(item => item.quantity > 0)
                .map(item => ({
                    inventoryItemId: item.inventoryItemId,
                    quantity: item.quantity
                }));

            if (updatedOrderItems.length === 0) {
                throw new Error('El pedido debe contener al menos un item');
            }

            // Preparar datos del pedido
            const orderData: Omit<ExtendedOrder, 'id'> = {
                items: updatedOrderItems,
                routeGroupId: currentRouteGroup.id,
                ...(currentRoute?.id && { routeId: currentRoute.id }),
            };

            // let savedOrder: ExtendedOrder;

            // if (existingOrder?.id) {
            //     const orderRef = doc(db, 'orders', existingOrder.id);
            //     await setDoc(orderRef, orderData, { merge: true });
            //     savedOrder = { ...orderData, id: existingOrder.id };
            // } else {
            //     const docRef = await addDoc(collection(db, 'orders'), orderData);
            //     savedOrder = { ...orderData, id: docRef.id };
            // }

            await updateInventory(updatedOrderItems, existingOrder?.items || []);

            // Iniciar transacción de Firebase
            // const batch = writeBatch(db);

            // Actualizar ruta y grupo de rutas si es necesario
            if (currentRoute?.id && currentRouteGroup) {
                const routeRef = doc(db, 'routes', currentRoute.id);
                // Preparar los datos de la ruta actualizados, omitiendo campos undefined
                const updatedRoute = {
                    ...currentRoute,
                    note_admin: noteAdmin.trim(),
                    note_driver: noteDriver.trim(),
                    order: { items: updatedOrderItems },
                    lastUpdated: new Date(),
                    existingFileUrl: existingFileUrl || undefined,
                    container_values: formData.containerValues.filter(value => value.trim() !== ''),
                    lenght_container_values: formData.containerValues.length,
                    deliveryFileUrl: existingFileUrl || undefined,
                };
                console.log('updatedRoute', updatedRoute);
                // batch.set(routeRef, updatedRoute, { merge: true });

                // Actualizar RouteGroup
                const updatedRouteGroup = { ...currentRouteGroup };
                const routeIndex = updatedRouteGroup.routes.findIndex(r => r.clientId === currentRoute.clientId);

                if (routeIndex !== -1) {
                    updatedRouteGroup.routes[routeIndex] = updatedRoute;
                }

                // Actualizar routesUnion manteniendo la estructura de tupla
                    if (updatedRouteGroup.routesUnion) {
                        const updatedRoutesUnion = updatedRouteGroup.routesUnion.map(routeUnion => {
                            const routes = routeUnion[0];
                            const date = routeUnion[1];
                            const flag = routeUnion[2];

                            const updatedRoutes = routes.map(route => {
                                if (route.id === currentRoute.id) {
                                    return updatedRoute;
                                }
                                return route;
                            });

                            // Mantener la estructura de tupla [Route[], Date, boolean]
                            return [updatedRoutes, date, flag] as [Route[], Date, boolean];
                        });

                        // Actualizar routesUnion con el formato de tupla
                        updatedRouteGroup.routesUnion = updatedRoutesUnion;
                }

                // const routeGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);
                // batch.set(routeGroupRef, updatedRouteGroup, { merge: true })

                // // Ejecutar la transacción
                // await batch.commit();

                // Actualizar estado local después de confirmar la transacción
                // if (onRouteGroupUpdate) {
                //     await onRouteGroupUpdate(updatedRouteGroup);
                // }
            } 
            // else {
                // Si solo se actualiza la orden
                // await batch.commit();
            // }

            // Notificar éxito y cerrar
            await onOrderCreated(
                orderData,
                noteAdmin,
                noteDriver,
                existingFileUrl || undefined,
                formData.containerValues.filter(value => value.trim() !== ''),
                formData.numContainers.toString()
            );

            setShowSuccessDialog(true);
            await new Promise(resolve => setTimeout(resolve, 800));

            // Ejecutar handleSaveRouteGroup después de cerrar el modal
            // if (currentRouteGroup && currentRoute) {
            //     const batch = writeBatch(db);
            //     const routeGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);

            //     // Preparar un objeto seguro para actualización
            //     const updateData = {
            //         name: currentRouteGroup.name,
            //         dayOfWeek: currentRouteGroup.dayOfWeek,
            //         weekOfMonth: currentRouteGroup.weekOfMonth,
            //         routeType: currentRouteGroup.routeType,
            //         createdAt: currentRouteGroup.createdAt,
            //     };

            //     // Actualiza la información del grupo de rutas
            //     batch.update(routeGroupRef, updateData);

            //     const routesRef = collection(routeGroupRef, 'routesUnion');
            //     const existingUnionIndex = currentRouteGroup.routesUnion.findIndex(([_, date]) => {
            //         const routeDate = date instanceof Timestamp ? date.toDate() : date;
            //         return routeDate && routeDate.toDateString() === currentRoute.fecha_pedido?.toDateString();
            //     });

            //     if (existingUnionIndex !== -1) {
            //         const routesUnionSnapshot = await getDocs(routesRef);
            //         const matchingDoc = routesUnionSnapshot.docs.find(doc => {
            //             const docData = doc.data();
            //             const docDate = docData.date instanceof Timestamp ? docData.date.toDate() : docData.date;
            //             return docDate && docDate.toDateString() === currentRoute.fecha_pedido?.toDateString();
            //         });

            //         if (matchingDoc) {
            //             const updatedRoutes = currentRouteGroup.routesUnion[existingUnionIndex][0];

            //             const updatedUnionEntry = {
            //                 routes: updatedRoutes.map((route, index) => ({
            //                     ...route,
            //                     orderIndex: index + 1
            //                 })),
            //                 date: currentRoute.fecha_pedido
            //             };

            //             const existingUnionRef = doc(routesRef, matchingDoc.id);
            //             batch.update(existingUnionRef, updatedUnionEntry);

            //             currentRouteGroup.routesUnion[existingUnionIndex] = [
            //                 updatedUnionEntry.routes,
            //                 currentRoute.fecha_pedido || new Date(),
            //                 true
            //             ];

            //             await batch.commit();
            //             toast.success('Rutas actualizadas exitosamente');
            //         }
            //     }
            // }
            setShowSuccessDialog(false);
            onClose();
        } catch (error) {
            console.error('Error al guardar el pedido:', error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Error al guardar el pedido. Por favor, inténtalo de nuevo.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const saveOrder = async (order: Order): Promise<string> => {
        const orderRef = collection(db, 'orders');
        const docRef = await addDoc(orderRef, order);
        return docRef.id;
    };

    const updateInventory = async (newItems: OrderItem[], oldItems: OrderItem[] = []) => {
        const batch = writeBatch(db);

        const oldQuantities = new Map(oldItems.map(item => [item.inventoryItemId, item.quantity]));

        for (const item of newItems) {
            const itemRef = doc(db, 'inventoryItems', item.inventoryItemId);
            const itemDoc = await getDoc(itemRef);

            if (!itemDoc.exists()) {
                console.warn(`El item con ID ${item.inventoryItemId} no existe en el inventario.`);
                continue;
            }

            const oldQuantity = oldQuantities.get(item.inventoryItemId) || 0;
            const quantityDifference = oldQuantity - item.quantity;
            batch.update(itemRef, { quantity: increment(quantityDifference) });
        }

        for (const oldItem of oldItems) {
            if (!newItems.some(newItem => newItem.inventoryItemId === oldItem.inventoryItemId)) {
                const itemRef = doc(db, 'inventoryItems', oldItem.inventoryItemId);
                const itemDoc = await getDoc(itemRef);

                if (!itemDoc.exists()) {
                    console.warn(`El item con ID ${oldItem.inventoryItemId} no existe en el inventario.`);
                    continue;
                }

                batch.update(itemRef, { quantity: increment(oldItem.quantity) });
            }
        }

        await batch.commit();
    };

    const calculateTotalOrderPrice = useCallback(() => {
        return orderItems.reduce((total, item) => {
            const inventoryItem = inventoryItems.find(ii => ii.id === item.inventoryItemId);
            return total + (inventoryItem ? inventoryItem.price * item.quantity : 0);
        }, 0);
    }, [orderItems, inventoryItems]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC' }).format(amount);
    };

    const handleImageClick = useCallback((imageUrl: string) => {
        setFullScreenImageUrl(imageUrl);
        setFullScreenImageOpen(true);
    }, []);

    const handleFullScreenImageClose = useCallback(() => {
        setFullScreenImageOpen(false);
    }, []);

    const filteredItems = inventoryItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (categoryFilter === '' || item.category === categoryFilter)
    );

    const categories = Array.from(new Set(inventoryItems.map(item => item.category)));

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
                style: {
                    minHeight: '94vh',
                    minWidth: '90vw',
                    borderRadius: 8,
                }
            }}>
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    {existingOrder ? 'Editar Orden' : 'Crear Orden'}
                </DialogTitle>
                <DialogContent sx={{
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(135, 206, 235, 0.5)',
                        borderRadius: '4px',
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(135, 206, 235, 0.7)',
                    },
                }}>
                    <Grid container spacing={2}>
                        <Grid item xs={8}>
                            <Paper
                                elevation={3}
                                sx={{
                                    mt: 2,
                                    p: 3,
                                    bgcolor: 'rgba(135, 206, 235, 0.1)',
                                    borderRadius: 4,
                                    border: '1px solid rgba(135, 206, 235, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(135, 206, 235, 0.2)',
                                        boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '87%' // Ensure the Paper takes up the full height of the Grid item
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <TextField
                                        label="Buscar"
                                        variant="outlined"
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            endAdornment: <SearchIcon />
                                        }}
                                    />
                                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Categoría</InputLabel>
                                        <Select
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value as string)}
                                            label="Categoría"
                                        >
                                            <MenuItem value="">Todas</MenuItem>
                                            {categories.map((category) => (
                                                <MenuItem key={category} value={category}>{category}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <TableContainer component={Paper} sx={{
                                    maxHeight: 'calc(100vh - 400px)', // Adjusted maxHeight to accommodate the Paper's padding and margin
                                    overflow: 'auto',
                                    flexGrow: 1, // Allow the table to grow and take up remaining space
                                    display: 'flex',
                                    flexDirection: 'column',
                                    bgcolor: 'rgba(135, 206, 235, 0.1)', // Same background color as the Paper
                                    borderRadius: 4,
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(135, 206, 235, 0.5)',
                                        borderRadius: '4px',
                                    },
                                    '&:hover::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(135, 206, 235, 0.7)',
                                    },
                                }}>
                                    <Table stickyHeader size="small">
                                        <TableHead sx={{ bgcolor: 'rgba(135, 206, 235, 0.3)' }}>
                                            <TableRow>
                                                <StyledTableCell width="60px" sx={{ fontWeight: 'bold', color: '#333' }}>Imagen</StyledTableCell>
                                                <StyledTableCell width="25%" sx={{ fontWeight: 'bold', color: '#333' }}>Producto</StyledTableCell>
                                                <StyledTableCell width="15%" align="right" sx={{ fontWeight: 'bold', color: '#333' }}>Precio</StyledTableCell>
                                                <StyledTableCell width="20%" align="center" sx={{ fontWeight: 'bold', color: '#333' }}>Cantidad</StyledTableCell>
                                                <StyledTableCell width="20%" align="right" sx={{ fontWeight: 'bold', color: '#333' }}>Total</StyledTableCell>
                                                <StyledTableCell width="60px" align="center" sx={{ fontWeight: 'bold', color: '#333' }}>Acciones</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredItems.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 3, fontWeight: 'bold', color: 'text.secondary' }}>
                                                        No se encontraron productos con los criterios de búsqueda.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredItems.map((item) => {
                                                    const orderItem = orderItems.find(oi => oi.inventoryItemId === item.id);
                                                    const quantity = orderItem?.quantity || 0;
                                                    const totalPrice = item.price * quantity;
                                                    return (
                                                        <TableRow key={item.id} hover>
                                                            <StyledTableCell>
                                                                <ProductImage
                                                                    src={item.imageUrl}
                                                                    alt={item.name}
                                                                    onClick={() => handleImageClick(item.imageUrl)}
                                                                />
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                {item.name}
                                                                <Typography variant="caption" display="block" color="text.secondary">
                                                                    Disponible: {availableQuantities[item.id]}
                                                                </Typography>
                                                            </StyledTableCell>
                                                            <StyledTableCell align="right">{formatCurrency(item.price)}</StyledTableCell>
                                                            <StyledTableCell align="center">
                                                                <IconButton
                                                                    onClick={() => handleQuantityChange(item.id, Math.max(0, quantity - 1))}
                                                                    disabled={quantity === 0}
                                                                    size="small"
                                                                >
                                                                    <RemoveIcon sx={{ color: '#5CACEE' }} />
                                                                </IconButton>
                                                                <TextField
                                                                    type="number"
                                                                    value={quantity}
                                                                    onChange={(e) => handleQuantityChange(item.id, Math.min(Math.max(0, parseInt(e.target.value) || 0), availableQuantities[item.id] + quantity))}
                                                                    inputProps={{
                                                                        min: 0,
                                                                        max: availableQuantities[item.id] + quantity,
                                                                    }}
                                                                    sx={{
                                                                        width: '40px',
                                                                        mx: 1,
                                                                        '& input': {
                                                                            padding: '4px',
                                                                            fontSize: '0.875rem',
                                                                            textAlign: 'center',
                                                                            MozAppearance: 'textfield',
                                                                            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                                                                                WebkitAppearance: 'none',
                                                                                margin: 0,
                                                                            },
                                                                        }
                                                                    }}
                                                                    size="small"
                                                                    disabled={quantity === 0 && availableQuantities[item.id] === 0}
                                                                />
                                                                <IconButton
                                                                    onClick={() => handleQuantityChange(item.id, Math.min(quantity + 1, availableQuantities[item.id] + quantity))}
                                                                    disabled={quantity === availableQuantities[item.id] + quantity}
                                                                    size="small"
                                                                >
                                                                    <AddIcon sx={{ color: '#5CACEE' }} />
                                                                </IconButton>
                                                            </StyledTableCell>
                                                            <StyledTableCell align="right">{formatCurrency(totalPrice)}</StyledTableCell>
                                                            <StyledTableCell align="center">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleQuantityChange(item.id, 0)}
                                                                    sx={{ color: 'rgb(255, 100, 100)' }}
                                                                    disabled={quantity === 0}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </StyledTableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <FixedBottomBox>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(135, 206, 235, 0.2)', borderRadius: 2, p: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                                            Total de la Orden:
                                        </Typography>
                                        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(calculateTotalOrderPrice())}
                                        </Typography>
                                    </Box>
                                </FixedBottomBox>
                            </Paper>
                        </Grid>
                        {/* Columna derecha para las notas */}
                        <Grid item xs={4}>
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Paper
                                    elevation={3} // Increased elevation for better visual separation
                                    sx={{
                                        p: 3, // Increased padding for more comfortable spacing
                                        bgcolor: 'rgba(135, 206, 235, 0.1)', // Celeste background with more transparency
                                        borderRadius: 4, // Slightly more rounded corners
                                        border: '1px solid rgba(135, 206, 235, 0.2)', // Lighter border
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            bgcolor: 'rgba(135, 206, 235, 0.2)', // Slightly darker on hover
                                            boxShadow: '0 6px 16px rgba(0,0,0,0.08)' // More pronounced shadow on hover
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h6" // Changed to h6 for better hierarchy
                                        gutterBottom
                                        sx={{
                                            fontWeight: 700, // Increased font weight
                                            color: '#5CACEE', // A slightly darker celeste color for the title
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 3, // Increased margin bottom
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <SettingsApplications sx={{ fontSize: 20 }} />
                                        Notas del Pedido
                                    </Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                                            Nota Administrativa
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={noteAdmin}
                                            onChange={(e) => setNoteAdmin(e.target.value)}
                                            placeholder="Agregar nota administrativa..."
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#87CEEB',
                                                    },
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#87CEEB',
                                                },
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                                            Nota para el Conductor
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={noteDriver}
                                            onChange={(e) => setNoteDriver(e.target.value)}
                                            placeholder="Agregar nota para el conductor..."
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#87CEEB',
                                                    },
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#87CEEB',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Paper>

                                <Paper
                                    elevation={3} // Increased elevation for better visual separation
                                    sx={{
                                        p: 3, // Increased padding for more comfortable spacing
                                        bgcolor: 'rgba(135, 206, 235, 0.1)', // Celeste background with more transparency
                                        borderRadius: 4, // Slightly more rounded corners
                                        border: '1px solid rgba(135, 206, 235, 0.2)', // Lighter border
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            bgcolor: 'rgba(135, 206, 235, 0.2)', // Slightly darker on hover
                                            boxShadow: '0 6px 16px rgba(0,0,0,0.08)' // More pronounced shadow on hover
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h6" // Changed to h6 for better hierarchy
                                        gutterBottom
                                        sx={{
                                            fontWeight: 700, // Increased font weight
                                            color: '#5CACEE', // A slightly darker celeste color for the title
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 3, // Increased margin bottom
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <LocalShipping sx={{ fontSize: 24 }} />
                                        Comprobante de Entrega
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 2
                                        }}
                                    >
                                        <input
                                            accept="image/*,.pdf"
                                            style={{ display: 'none' }}
                                            id="delivery-file-upload"
                                            type="file"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    // Verificar el tamaño (máximo 5MB)
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        toast.error('El archivo no debe superar los 5MB');
                                                        return;
                                                    }

                                                    // Verificar el tipo de archivo
                                                    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
                                                    if (!validTypes.includes(file.type)) {
                                                        toast.error('Formato no válido. Use: JPG, PNG, GIF o PDF');
                                                        return;
                                                    }

                                                    setExistingFileUrl(URL.createObjectURL(file));
                                                    // Aquí puedes manejar el archivo válido
                                                    setFormData({
                                                        ...formData,
                                                        deliveryFile: file
                                                    });
                                                }
                                            }}
                                        />

                                        {existingFileUrl && (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2,
                                                width: '100%'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    color: 'text.secondary',
                                                    width: '100%',
                                                    bgcolor: 'rgba(0, 180, 216, 0.05)',
                                                    p: 2,
                                                    borderRadius: 1
                                                }}>
                                                    <AttachFile sx={{ fontSize: 20 }} />
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                        Comprobante actual
                                                    </Typography>
                                                    <Link
                                                        href={existingFileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{
                                                            color: 'primary.main',
                                                            textDecoration: 'none',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        Ver archivo
                                                    </Link>
                                                </Box>

                                                {/* Preview for existing image */}
                                                {existingFileUrl.match(/\.(jpg|jpeg|png|gif)$/i) && (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            maxHeight: '200px',
                                                            overflow: 'hidden',
                                                            borderRadius: 2,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        <img
                                                            src={existingFileUrl}
                                                            alt="Comprobante actual"
                                                            style={{
                                                                width: '100%',
                                                                height: '200px',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </Box>
                                                )}

                                                {/* Preview for existing PDF */}
                                                {existingFileUrl.match(/\.pdf$/i) && (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            p: 2,
                                                            bgcolor: 'background.paper',
                                                            borderRadius: 2,
                                                            border: '1px solid rgba(0, 180, 216, 0.2)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2
                                                        }}
                                                    >
                                                        <PictureAsPdf sx={{ fontSize: 40, color: 'error.main' }} />
                                                        <Box>
                                                            <Typography variant="subtitle2" color="text.primary">
                                                                Documento PDF
                                                            </Typography>
                                                            <Link
                                                                href={existingFileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: 'primary.main',
                                                                    textDecoration: 'none',
                                                                    '&:hover': { textDecoration: 'underline' }
                                                                }}
                                                            >
                                                                Ver PDF
                                                            </Link>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}


                                        {formData.deliveryFile && (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2,
                                                width: '100%'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    color: 'text.secondary',
                                                    width: '100%',
                                                    bgcolor: 'rgba(0, 180, 216, 0.05)',
                                                    p: 2,
                                                    borderRadius: 1
                                                }}>
                                                    <AttachFile sx={{ fontSize: 20 }} />
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                        {formData.deliveryFile.name}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setFormData({
                                                                ...formData,
                                                                deliveryFile: null
                                                            });
                                                        }}
                                                        sx={{
                                                            color: 'error.main',
                                                            '&:hover': {
                                                                bgcolor: 'error.light',
                                                                color: 'error.contrastText'
                                                            }
                                                        }}
                                                    >
                                                        <Clear sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>

                                                {/* Vista previa de imagen */}
                                                {formData.deliveryFile.type.startsWith('image/') && (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            maxHeight: '200px',
                                                            overflow: 'hidden',
                                                            borderRadius: 2,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        <img
                                                            src={URL.createObjectURL(formData.deliveryFile)}
                                                            alt="Vista previa"
                                                            style={{
                                                                width: '100%',
                                                                height: '200px',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </Box>
                                                )}

                                                {/* Vista previa de PDF */}
                                                {formData.deliveryFile.type === 'application/pdf' && (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            p: 2,
                                                            bgcolor: 'background.paper',
                                                            borderRadius: 2,
                                                            border: '1px solid rgba(0, 180, 216, 0.2)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2
                                                        }}
                                                    >
                                                        <PictureAsPdf sx={{ fontSize: 40, color: 'error.main' }} />
                                                        <Box>
                                                            <Typography variant="subtitle2" color="text.primary">
                                                                Documento PDF
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {(formData.deliveryFile.size / 1024 / 1024).toFixed(2)} MB
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <label htmlFor="delivery-file-upload">
                                            <Button
                                                component="span"
                                                variant={existingFileUrl ? "outlined" : "contained"}
                                                startIcon={existingFileUrl ? <Edit /> : <Upload />}
                                                sx={{
                                                    fontSize: '13px',
                                                    borderColor: existingFileUrl ? 'rgba(0, 180, 216, 0.5)' : undefined,
                                                    color: existingFileUrl ? 'rgb(0, 180, 216)' : 'white',
                                                    bgcolor: existingFileUrl ? 'transparent' : 'rgb(0, 180, 216)',
                                                    '&:hover': {
                                                        borderColor: existingFileUrl ? 'rgb(0, 180, 216)' : undefined,
                                                        bgcolor: existingFileUrl
                                                            ? 'rgba(0, 180, 216, 0.05)'
                                                            : 'rgb(0, 119, 182)'
                                                    }
                                                }}
                                            >
                                                {existingFileUrl ? 'Cambiar comprobante' : 'Subir Comprobante'}
                                            </Button>
                                        </label>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            align="center"
                                            sx={{ mt: 1 }}
                                        >
                                            Formatos permitidos: JPG, PNG, GIF, PDF (máx. 5MB)
                                        </Typography>
                                    </Box>
                                </Paper>

                            </Box>
                        </Grid>
                    </Grid>
                    {/* Configuración de Entrega Section */}
                    <Paper
                        elevation={3} // Increased elevation for better visual separation
                        sx={{
                            p: 3, // Increased padding for more comfortable spacing
                            bgcolor: 'rgba(135, 206, 235, 0.1)', // Celeste background with more transparency
                            borderRadius: 4, // Slightly more rounded corners
                            border: '1px solid rgba(135, 206, 235, 0.2)', // Lighter border
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: 'rgba(135, 206, 235, 0.2)', // Slightly darker on hover
                                boxShadow: '0 6px 16px rgba(0,0,0,0.08)' // More pronounced shadow on hover
                            },
                            mt: -8,
                        }}
                    >
                        <Typography
                            variant="h6" // Changed to h6 for better hierarchy
                            gutterBottom
                            sx={{
                                fontWeight: 700, // Increased font weight
                                color: '#5CACEE', // A slightly darker celeste color for the title
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 3, // Increased margin bottom
                                justifyContent: 'center'
                            }}
                        >
                            <SettingsApplications sx={{ fontSize: 28 }} /> {/* Increased icon size */}
                            Configuración de Entrega
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3, // Increased gap
                            mb: 3 // Increased margin bottom
                        }}>
                            <IconButton
                                onClick={() => {
                                    const newNum = Math.max(formData.numContainers - 1, 1);
                                    setFormData({
                                        ...formData,
                                        numContainers: newNum,
                                        containerValues: Array(newNum).fill('').map((_, i) => formData.containerValues[i] || '')
                                    });
                                }}
                                disabled={formData.numContainers <= 1}
                                sx={{
                                    '&:hover': { bgcolor: 'rgba(135, 206, 235, 0.3)' }, // Celeste hover color
                                    color: '#5CACEE' // A slightly darker celeste color
                                }}
                                aria-label="Reducir contenedores"
                            >
                                <RemoveCircleOutline sx={{ fontSize: 28 }} /> {/* Increased icon size */}
                            </IconButton>

                            <TextField
                                fullWidth
                                label="Cantidad de Contenedores" // More descriptive label
                                value={formData.numContainers}
                                variant="outlined"
                                InputProps={{
                                    startAdornment: <Box component="span" sx={{ mr: 1, fontSize: '240%' }}>📦</Box>,
                                    inputProps: {
                                        min: 1,
                                        max: 8,
                                        style: { textAlign: 'center', padding: '10px' } // Centered text and added padding
                                    }
                                }}
                                helperText={`Máximo 8 contenedores (${formData.numContainers}/8)`}
                                onChange={(e) => {
                                    const num = Math.min(Math.max(parseInt(e.target.value) || 1, 1), 8);
                                    setFormData({
                                        ...formData,
                                        numContainers: num,
                                        containerValues: Array(num).fill('').map((_, i) => formData.containerValues[i] || '')
                                    });
                                }}
                            />

                            <IconButton
                                onClick={() => {
                                    const newNum = Math.min(formData.numContainers + 1, 8);
                                    setFormData({
                                        ...formData,
                                        numContainers: newNum,
                                        containerValues: Array(newNum).fill('').map((_, i) => formData.containerValues[i] || '')
                                    });
                                }}
                                disabled={formData.numContainers >= 8}
                                sx={{
                                    '&:hover': { bgcolor: 'rgba(135, 206, 235, 0.3)' }, // Celeste hover color
                                    color: '#5CACEE' // A slightly darker celeste color
                                }}
                                aria-label="Agregar contenedor"
                            >
                                <AddCircleOutline sx={{ fontSize: 28 }} /> {/* Increased icon size */}
                            </IconButton>
                        </Box>
                        <Grid container spacing={4}> {/* Increased spacing */}
                            {Array.from({ length: formData.numContainers }).map((_, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={`Contenedor ${index + 1} (Kg)`} // Added units to label
                                        value={formData.containerValues[index] || ''}
                                        variant="outlined"
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <Chip
                                                    label={index + 1}
                                                    size="small"
                                                    color="primary"
                                                    sx={{
                                                        mr: 1,
                                                        fontWeight: 600,
                                                        boxShadow: '0 3px 6px rgba(0,0,0,0.15)' // Increased shadow
                                                    }}
                                                />
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: 'text.secondary',
                                                            userSelect: 'none'
                                                        }}
                                                    >
                                                        Kg
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                            inputProps: {
                                                min: 0,
                                                step: "0.01",
                                                style: { textAlign: 'right', paddingRight: '45px', padding: '10px' } // Added padding
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    boxShadow: '0 3px 10px rgba(0,0,0,0.1)' // Increased shadow
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 5px 14px rgba(0,0,0,0.12)' // Increased shadow
                                                }
                                            },
                                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                                display: 'none'
                                            },
                                            '& input[type=number]': {
                                                MozAppearance: 'textfield'
                                            }
                                        }}
                                        helperText="Ingrese el peso en kilogramos"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*\.?\d*$/.test(value)) {
                                                const newValues = [...formData.containerValues];
                                                newValues[index] = value;
                                                setFormData({ ...formData, containerValues: newValues });
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: 'right',
                    px: 3,
                    py: 2,
                    backgroundColor: 'rgba(135, 206, 235, 0.1)'
                }}>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        sx={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#5CACEE',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}
                        // disabled={isLoading || !formData.containerValues.every(val => val.trim() !== '')}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Guardar'}
                    </Button>
                    <Button onClick={onClose} variant="outlined"
                        sx={{
                            color: '#87CEEB',
                            borderColor: '#87CEEB',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                borderColor: '#5CACEE',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}
                        disabled={isLoading}>Cancelar</Button>
                </DialogActions>
                <FullScreenImageModal
                    open={fullScreenImageOpen}
                    onClose={handleFullScreenImageClose}
                    closeAfterTransition
                >
                    <Fade in={fullScreenImageOpen}>
                        <FullScreenImage
                            src={fullScreenImageUrl}
                            alt="Product"
                            onClick={handleFullScreenImageClose}
                        />
                    </Fade>
                </FullScreenImageModal>
            </Dialog>
            <Dialog open={showSuccessDialog} aria-labelledby="success-dialog-title">
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <SuccessIcon />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default OrderCreationModalAutomatic;